/**
 * Utility helpers for RTK Query query results.
 *
 * These helpers ensure that a backend error (500, timeout, network failure)
 * is NEVER silently rendered as an empty list. Components should use
 * `extractListData` instead of `data?.data || []` so that:
 *  - while loading, the list is empty (no flash of stale content)
 *  - on error, the error is visible and the list stays empty
 *  - on success, the list is populated
 *
 * Usage:
 *   const { data, error, isLoading } = useGetClientsQuery(...);
 *   const clients = extractListData(data, error, isLoading);
 *   if (error) return <ErrorState error={error} />;
 */

/**
 * Safely extract a list from an RTK Query response.
 * Returns [] only when there is no error. If error is present,
 * the caller should check `error` separately before using this.
 *
 * @param {object|undefined} data - RTK Query response data
 * @param {object|undefined} error - RTK Query error object
 * @param {boolean} isLoading - whether the query is still loading
 * @returns {array} the list data, or [] if loading/empty
 */
export const extractListData = (data, error, isLoading) => {
  if (isLoading) return [];
  if (error) return []; // caller must check error separately
  if (!data) return [];
  // Backend returns { status, count, data: [...] } or just [...]
  if (Array.isArray(data)) return data;
  if (data.data && Array.isArray(data.data)) return data.data;
  if (data.results && Array.isArray(data.results)) return data.results;
  return [];
};

/**
 * Safely extract a single object from an RTK Query response.
 */
export const extractObjectData = (data, error, isLoading) => {
  if (isLoading) return null;
  if (error) return null;
  if (!data) return null;
  if (data.data) return data.data;
  return data;
};

/**
 * Format an RTK Query error into a human-readable message.
 */
export const formatQueryError = (error) => {
  if (!error) return null;
  if (error.status === 'FETCH_ERROR') {
    return 'Erreur réseau : impossible de contacter le serveur.';
  }
  if (error.status === 'TIMEOUT') {
    return 'La requête a expiré. Veuillez réessayer.';
  }
  if (error.status === 401) {
    return 'Session expirée. Veuillez vous reconnecter.';
  }
  if (error.status === 403) {
    return "Vous n'avez pas accès à cette ressource.";
  }
  if (error.status === 404) {
    return 'Ressource introuvable.';
  }
  if (error.status >= 500) {
    return 'Erreur serveur. Veuillez réessayer ultérieurement.';
  }
  if (error.data?.message) {
    return error.data.message;
  }
  if (error.data?.detail) {
    return error.data.detail;
  }
  return 'Une erreur est survenue lors du chargement des données.';
};
